import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import type { ErrorComponentProps } from '@tanstack/react-router';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { getApiErrorMessage } from '@/utils/errors';

interface RouteErrorFallbackProps extends ErrorComponentProps {
	fallbackMessageKey?: ParseKeys;
}

export function RouteErrorFallback({ error, reset, fallbackMessageKey }: RouteErrorFallbackProps) {
	const { t } = useTranslation();
	const { reset: resetQueries } = useQueryErrorResetBoundary();

	function handleRetry() {
		resetQueries();
		// `reset` is typed non-optional but TanStack Router passes `undefined` for
		// loader/beforeLoad errors (only render-time errors get a real reset fn).
		reset?.();
	}

	const fallback = fallbackMessageKey ? t(fallbackMessageKey) : t('route_error_description');

	return (
		<div className='flex flex-col items-start gap-3'>
			<div className='space-y-1'>
				<p className='font-medium text-sm'>{t('route_error_title')}</p>
				<p className='text-destructive text-sm'>{getApiErrorMessage(error, fallback)}</p>
			</div>
			<Button variant='outline' size='sm' onClick={handleRetry}>
				{t('route_error_retry')}
			</Button>
		</div>
	);
}
